// Reiter „Dateien“ der linken Lead-Spalte wie in Close: alle Anhänge der Aktivitäten des Leads (E-Mail, Notiz, SMS)
// mit Name, Art, Größe, Datum und Quelle. Klick öffnet die Datei über den Server (kein Close-Schlüssel im Browser).
// Hochladen per Knopf oder Ziehen: der Server legt die Datei bei Close ab und trägt eine Notiz mit Anhang am Lead ein.
// Leerer Zustand wie Close: Symbol, „Keine Dateien“, Hinweis, Knopf „Datei hochladen“.
import { useCallback, useEffect, useRef, useState, type DragEvent } from "react";
import { dateienApi, type DateienListe, type LeadDatei } from "../../api/dateien";
import { fehlerText } from "../../api/client";
import { useMeldung } from "../Meldungen";
import { zeitpunktText } from "../../werkzeuge/zeit";
import "../../styles/linke-spalte.css";

const QUELLE: Record<LeadDatei["quelle"], string> = { email: "E-Mail", notiz: "Notiz", sms: "SMS" };

export function groesseText(byte: number | null | undefined): string {
  if (byte === null || byte === undefined) return "";
  if (byte < 1024) return `${byte} B`;
  if (byte < 1024 * 1024) return `${Math.round(byte / 1024)} KB`;
  return `${(byte / 1024 / 1024).toLocaleString("de-DE", { maximumFractionDigits: 1 })} MB`;
}

function quelleText(d: LeadDatei): string {
  if (d.quelle === "email") return d.richtung === "incoming" ? "E-Mail erhalten" : "E-Mail gesendet";
  if (d.quelle === "sms") return d.richtung === "inbound" ? "SMS erhalten" : "SMS";
  return QUELLE.notiz;
}

/** Dateisymbol im farbigen Kästchen (Farbe nach Art). */
export function DateiSymbol({ art }: { art: string }) {
  return (
    <span className={`ls-datei__symbol ls-datei__symbol--${art.toLowerCase()}`} aria-hidden="true">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
        <path d="M14 3v5h5" />
      </svg>
    </span>
  );
}

function DateiZeile({ leadId, d }: { leadId: string; d: LeadDatei }) {
  const meta = [d.art, groesseText(d.size), d.date ? zeitpunktText(d.date) : "", quelleText(d), d.user_name].filter(Boolean).join(" · ");
  const inhalt = (
    <>
      <DateiSymbol art={d.art} />
      <span className="ls-datei__text">
        <span className="ls-datei__name">{d.name}</span>
        <span className="ls-datei__meta">{meta}</span>
        {d.titel ? <span className="ls-datei__titel">{d.titel}</span> : null}
      </span>
    </>
  );
  if (!d.herunterladbar) {
    return (
      <li className="ls-datei ls-datei--stumm" title="Nur im CRM gesendet, die Datei liegt hier nicht zum Abruf.">
        <div className="ls-datei__zeile">{inhalt}</div>
      </li>
    );
  }
  return (
    <li className="ls-datei">
      <a className="ls-datei__zeile" href={dateienApi.url(leadId, d.id)} target="_blank" rel="noopener noreferrer" title={`${d.name} öffnen`}>
        {inhalt}
      </a>
      <a className="symbolknopf symbolknopf--klein ls-datei__laden" href={dateienApi.url(leadId, d.id, true)} download={d.name} title="Herunterladen" aria-label={`${d.name} herunterladen`}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 4v11M7 11l5 5 5-5M5 20h14" />
        </svg>
      </a>
    </li>
  );
}

export function DateienReiter({ leadId }: { leadId: string }) {
  const meldung = useMeldung();
  const [liste, setListe] = useState<DateienListe | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [laeuft, setLaeuft] = useState(false);
  const [ziehen, setZiehen] = useState(false);
  const eingabe = useRef<HTMLInputElement>(null);
  const zaehler = useRef(0);

  const laden = useCallback(async () => {
    try {
      setListe(await dateienApi.liste(leadId));
      setFehler(null);
    } catch (e) {
      setFehler(fehlerText(e));
    } finally {
      setLaedt(false);
    }
  }, [leadId]);

  useEffect(() => {
    setLaedt(true);
    setListe(null);
    void laden();
  }, [laden]);

  const hochladen = async (dateien: File[]) => {
    if (!dateien.length) return;
    const max = liste?.max_bytes ?? 200 * 1024;
    setLaeuft(true);
    let ok = 0;
    for (const f of dateien) {
      if (f.size > max) {
        meldung.zeige("fehler", `${f.name} ist zu groß (höchstens ${groesseText(max)}).`);
        continue;
      }
      try {
        await dateienApi.hochladen(leadId, f);
        ok += 1;
      } catch (e) {
        meldung.fehler(e, `${f.name} nicht hochgeladen`);
      }
    }
    setLaeuft(false);
    if (ok) {
      meldung.ok(ok === 1 ? "Datei hochgeladen, Notiz am Lead angelegt." : `${ok} Dateien hochgeladen.`);
      await laden();
    }
  };

  const ablegen = (e: DragEvent) => {
    e.preventDefault();
    zaehler.current = 0;
    setZiehen(false);
    void hochladen(Array.from(e.dataTransfer.files));
  };
  const istDatei = (e: DragEvent) => Array.from(e.dataTransfer.types).includes("Files");

  const knopf = (
    <>
      <input
        ref={eingabe}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          void hochladen(Array.from(e.target.files ?? []));
          e.target.value = "";
        }}
      />
      <button type="button" className="knopf knopf--klein" disabled={laeuft} onClick={() => eingabe.current?.click()}>
        {laeuft ? "Lädt hoch …" : "Datei hochladen"}
      </button>
    </>
  );

  const daten = liste?.data ?? [];
  return (
    <div
      className={`ls-dateien${ziehen ? " ls-dateien--ziehen" : ""}`}
      onDragEnter={(e) => {
        if (!istDatei(e)) return;
        e.preventDefault();
        zaehler.current += 1;
        setZiehen(true);
      }}
      onDragOver={(e) => istDatei(e) && e.preventDefault()}
      onDragLeave={(e) => {
        if (!istDatei(e)) return;
        zaehler.current -= 1;
        if (zaehler.current <= 0) setZiehen(false);
      }}
      onDrop={ablegen}
    >
      {fehler ? (
        <p className="ls-leer ls-dateien__fehler">
          {fehler}{" "}
          <button type="button" className="textknopf" onClick={() => void laden()}>
            Nochmal
          </button>
        </p>
      ) : laedt ? (
        <p className="ls-leer">Dateien werden geladen …</p>
      ) : daten.length === 0 ? (
        <div className="ls-dateien__leer">
          <span className="ls-dateien__leersymbol" aria-hidden="true">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" stroke="none">
              <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z" />
              <path d="M14 2v5h5" fill="rgba(255,255,255,0.55)" />
            </svg>
          </span>
          <strong>Keine Dateien</strong>
          <p>Wenn du Dateien sendest oder erhältst oder sie an Aktivitäten hängst, erscheinen sie hier. Dateien lassen sich auch hierher ziehen.</p>
          {knopf}
        </div>
      ) : (
        <>
          <div className="ls-dateien__kopf">
            <span className="ls-dateien__zahl">{daten.length === 1 ? "1 Datei" : `${daten.length} Dateien`}</span>
            {knopf}
          </div>
          <ul className="ls-dateien__liste">
            {daten.map((d) => (
              <DateiZeile key={d.id} leadId={leadId} d={d} />
            ))}
          </ul>
        </>
      )}
      {ziehen ? <div className="ls-dateien__ablegen">Zum Hochladen hier ablegen</div> : null}
    </div>
  );
}
